/*
 * Cart sync: keeps the native cart drawer in step with every add-to-cart source.
 *
 * Theme code (product form, quick add, drawer, upsell) asks the cart API for the
 * drawer HTML itself (the `sections` parameter) and updates the drawer directly.
 * Apps and custom snippets (Fast Bundle, Shop the Look / Lookify / Robin PRO,
 * pendant + chain upsell, builders) call /cart/add|update|change|clear without it.
 *
 * Detection uses a PerformanceObserver on network resources: it sees every completed
 * cart request, however an app sends it (other apps replace fetch / XMLHttpRequest,
 * so wrapping them alone misses some). After a cart change from outside the theme
 * it re-renders the drawer (one request) and opens it when items were added.
 *
 * The fetch/XHR wrapper is only used to:
 *   - open the drawer the moment an add starts (no waiting on Shopify's ~0.7s add)
 *   - skip refreshes the theme already does itself, and note saves (gift message)
 *
 * It also sends Klaviyo "Added to Cart" for every successful add, which Upcart did before.
 * Nothing here changes requests or responses, so Fast Bundle etc. behave exactly as before.
 */
(function () {
  if (window.__lnkCartSync) return;
  window.__lnkCartSync = true;

  var MUTATION = /\/cart\/(add|update|change|clear)$/;
  var nativeFetch = window.fetch.bind(window);

  var lastCount = typeof window.__lnkCartCount === 'number' ? window.__lnkCartCount : null;
  var pending = null;
  var shouldOpen = false;
  var openIfMore = false;
  var skip = {}; // path -> number of requests the theme refreshes itself
  var addsStarted = 0;
  var openedEarly = 0;
  var busyTimer = null;
  var earlyGuard = null;

  function pathOf(url) {
    try {
      return new URL(url, location.origin).pathname.replace(/\.(js|json)$/, '');
    } catch (e) {
      return '';
    }
  }

  function onCartPage() {
    return /^\/cart\/?$/.test(location.pathname);
  }

  function bodyHasSections(body) {
    if (!body) return false;
    if (typeof body === 'string') return /(^|[&"{,\s])sections["=]/.test(body);
    if (typeof FormData !== 'undefined' && body instanceof FormData) return body.has('sections');
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return body.has('sections');
    return false;
  }

  /** Note / attribute saves (gift message box) change nothing visible. */
  function isNoteOnly(path, body) {
    if (!/\/cart\/update$/.test(path) || typeof body !== 'string') return false;
    try {
      var keys = Object.keys(JSON.parse(body));
      return (
        keys.length > 0 &&
        keys.every(function (k) {
          return k === 'note' || k === 'attributes';
        })
      );
    } catch (e) {
      return /^(note|attributes)/.test(body) && !/updates/.test(body);
    }
  }

  /* ---------- drawer helpers ---------- */

  function drawer() {
    return document.querySelector('cart-drawer-component');
  }

  function dialog() {
    return document.querySelector('cart-drawer-component dialog');
  }

  function drawerSectionId() {
    var el = document.querySelector('cart-drawer-component cart-items-component');
    return el && el.getAttribute('data-section-id');
  }

  function setBusy(on) {
    var d = dialog();
    if (!d) return;
    clearTimeout(busyTimer);
    if (on) {
      d.setAttribute('aria-busy', 'true');
      busyTimer = setTimeout(function () {
        setBusy(false);
      }, 8000);
    } else {
      d.removeAttribute('aria-busy');
    }
  }

  /** Opens the drawer; returns true if it was closed before. */
  function openDrawer(busy) {
    var el = drawer();
    if (!el || typeof el.open !== 'function' || !el.hasAttribute('auto-open') || onCartPage()) return false;
    var d = dialog();
    var wasOpen = d && d.open;
    if (!wasOpen) el.open();
    if (busy) setBusy(true);
    return !wasOpen;
  }

  /** An add we opened early for didn't happen or failed: close again. */
  function cancelEarlyOpen() {
    setBusy(false);
    if (openedEarly > 0) {
      openedEarly = 0;
      var el = drawer();
      if (el && typeof el.close === 'function') el.close();
    }
  }

  function trackAddedToCart() {
    try {
      var learnq = (window._learnq = window._learnq || []);
      nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          learnq.push([
            'track',
            'Added to Cart',
            {
              total_price: data.total_price / 100,
              $value: data.total_price / 100,
              total_discount: data.total_discount,
              original_total_price: data.original_total_price / 100,
              items: data.items,
            },
          ]);
        })
        .catch(function () {});
    } catch (e) {}
  }

  /* ---------- refresh after outside changes ---------- */

  function refreshDrawer() {
    var sectionId = drawerSectionId();
    var open = shouldOpen;
    var openWhenMore = openIfMore;
    shouldOpen = false;
    openIfMore = false;

    if (open) openDrawer(true);

    if (!sectionId) {
      document.dispatchEvent(new CustomEvent('shopify:cart:update'));
      return;
    }

    nativeFetch(location.pathname + '?sections=' + encodeURIComponent(sectionId), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        var html = sections && sections[sectionId];
        var count = null;
        if (html) {
          var m = html.match(/ref="cartItemCount"[^>]*>\s*(\d+)\s*</);
          count = m ? parseInt(m[1], 10) : 0;
        }
        if (!open && openWhenMore && count !== null && lastCount !== null && count > lastCount) openDrawer(false);
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: 'cart-sync',
              data: {
                source: 'cart-sync',
                itemCount: count === null ? undefined : count,
                sections: html ? sections : undefined,
              },
            },
          })
        );
      })
      .catch(function () {
        setBusy(false);
      });
  }

  function onCartRequestDone(path, failed) {
    var isAdd = /\/cart\/add$/.test(path);
    if (isAdd) {
      clearTimeout(earlyGuard); // an add happened (some apps bypass the fetch wrapper)
      if (failed) return cancelEarlyOpen();
      trackAddedToCart();
    }
    if (failed) return;
    clearTimeout(earlyGuard);
    if (skip[path] > 0) {
      skip[path]--;
      return;
    }
    if (isAdd) shouldOpen = true;
    // Shop the Look apps add through /cart/update.js: open only if the cart grew.
    else if (/\/cart\/update$/.test(path)) openIfMore = true;
    clearTimeout(pending);
    // Apps often fire several calls in a row; refresh once they settle.
    pending = setTimeout(refreshDrawer, 60);
  }

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
          var path = pathOf(entry.name);
          if (!MUTATION.test(path)) return;
          var status = entry.responseStatus; // 0 / undefined when the browser doesn't expose it
          onCartRequestDone(path, typeof status === 'number' && status >= 400);
        });
      }).observe({ type: 'resource', buffered: false });
    } catch (e) {}
  }

  /* ---------- open instantly + mark requests the theme refreshes itself ---------- */

  function beforeRequest(url, method, body) {
    if (!method || String(method).toUpperCase() === 'GET') return;
    var path = pathOf(url);
    if (!MUTATION.test(path)) return;
    if (bodyHasSections(body) || isNoteOnly(path, body)) skip[path] = (skip[path] || 0) + 1;
    if (/\/cart\/add$/.test(path)) {
      addsStarted++;
      if (openDrawer(true)) openedEarly++;
    }
  }

  window.fetch = function (input, init) {
    var clearing = false;
    try {
      var url = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
      var method = (init && init.method) || (input && input.method) || 'GET';
      beforeRequest(url, method, init && init.body);
      clearing = /\/cart\/clear$/.test(pathOf(url)) && String(method).toUpperCase() !== 'GET';
    } catch (e) {}
    var request = nativeFetch(input, init);
    // /cart/clear redirects to /cart.js, so the resource timeline lists it under that name.
    if (clearing) {
      request
        .then(function (res) {
          onCartRequestDone('/cart/clear', !res.ok);
        })
        .catch(function () {});
    }
    return request;
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__lnkUrl = url;
    this.__lnkMethod = method;
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    try {
      beforeRequest(String(this.__lnkUrl || ''), this.__lnkMethod, body);
    } catch (e) {}
    return xhrSend.apply(this, arguments);
  };

  /* Theme product forms (PDP, quick add): open on the click itself. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.getAttribute) return;
      if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
      if (!form.closest('product-form-component')) return;
      if (form.checkValidity && !form.checkValidity()) return;
      var button = form.querySelector('[type="submit"]');
      if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) return;
      if (openDrawer(true)) openedEarly++;
      // If no add request follows (an app took over, validation), close again.
      clearTimeout(earlyGuard);
      var startedAt = addsStarted;
      earlyGuard = setTimeout(function () {
        if (addsStarted === startedAt) cancelEarlyOpen();
      }, 3000);
    },
    true
  );

  /* Keep the known item count fresh; clear the loading state once the drawer re-rendered. */
  document.addEventListener('cart:update', function (event) {
    var data = event.detail && event.detail.data;
    if (data && data.sections) {
      openedEarly = 0;
      setBusy(false);
    }
    if (data && data.source !== 'product-form-component' && typeof data.itemCount === 'number') {
      lastCount = data.itemCount;
    } else {
      nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (c) {
          lastCount = c.item_count;
        })
        .catch(function () {});
    }
  });
})();
